import { forwardRef } from 'react';
import { Link } from '@tanstack/react-router';
import type { LinkAsProps } from '@leanin/ui';

// `LinkAsProps` from packages/ui declares `href: string` — the contract was
// authored when next/link was the routing library. TanStack Router's `Link`
// takes `to` (pathname only) plus an optional `search` (typed query). When
// the `href` carries a `?query=…`, we MUST split it: passing the full
// string as `to` makes the router treat the literal `…?…` as the pathname
// and then re-append the current page's search state, producing malformed
// URLs like `/foo?a=b?c=d`. Splitting at the first `?` and routing the
// query string through `search` keeps the URL clean and lets the
// destination route's `validateSearch` parse the params.
export const RouterLinkAs = forwardRef<HTMLAnchorElement, LinkAsProps>(
  function RouterLinkAs({ href, ...rest }, ref) {
    const q = href.indexOf('?');
    if (q < 0) {
      return <Link ref={ref} to={href} {...rest} />;
    }
    const to = href.slice(0, q);
    const params = new URLSearchParams(href.slice(q + 1));
    const search: Record<string, string> = {};
    for (const [k, v] of params) search[k] = v;
    // TanStack's `search` is typed against the destination route's
    // schema; this adapter is generic (one `href: string` slot for any
    // route), so we cast through `unknown` to silence the structural
    // mismatch. The destination route's `validateSearch` still runs at
    // navigation time and is the real type boundary.
    return (
      <Link
        ref={ref}
        to={to}
        search={search as unknown as never}
        {...rest}
      />
    );
  },
);
